import { useMemo } from "react";
import {
  isGranted,
  type PermissionReason,
} from "@/features/auth/models/permissions";
import type { NavItem } from "../types";
import { type NavData, orderedHubItems, useNavData } from "./useNavTree";

export type BottomNavItem =
  | { kind: "menu" }
  | { kind: "hub"; item: NavItem; href: string }
  | { kind: "betslip" };

const bettableReasons: (PermissionReason | undefined)[] = [
  undefined,
  "GRANTED",
  "ACCOUNT_REOPEN_REQUIRED",
  "COOLDOWN",
];

// A Pick'ems player who can't bet loses the betting hubs, and the pill has no
// Pick'ems cell to replace them.
const buildBottomNavItems = (
  data: NavData,
  hasDock: boolean,
): BottomNavItem[] => {
  const { submitBet, viewContest } = data.permissions;
  const showsBettingHubs =
    bettableReasons.includes(submitBet) || !isGranted(viewContest);

  const hubs = orderedHubItems(data, { showsBettingHubs, showsLive: false });

  return [
    { kind: "menu" },
    ...hubs.flatMap((item): BottomNavItem[] =>
      item.target?.kind === "link"
        ? [
            {
              kind: "hub",
              item: { ...item, isNew: false },
              href: item.target.href,
            },
          ]
        : [],
    ),
    ...(hasDock ? [{ kind: "betslip" } as const] : []),
  ];
};

export const useBottomNavItems = (
  hasDock: boolean,
): { items: BottomNavItem[]; isSettled: boolean; skeletonCells: number } => {
  const { data, isSettled } = useNavData();
  const items = useMemo(
    () => buildBottomNavItems(data, hasDock),
    [data, hasDock],
  );

  return {
    items,
    isSettled,
    skeletonCells: 1 + data.hubs.length + (hasDock ? 1 : 0),
  };
};
